/**
 * Chat derived state hook
 * Calculates derived state values using useMemo
 */

import { useMemo } from 'react';
import type { UIMessage } from 'ai';
import type { ToolPartWithState } from '../utils/toolInvocations';
import { getToolRunningLine } from '../tool-display/tool-labels';

/** Stream part states that mean a tool invocation is still in flight. */
const IN_FLIGHT_TOOL_STATES = new Set(['input-streaming', 'input-available', 'approval-responded']);

function isToolPart(part: unknown): part is ToolPartWithState {
  const p = part as ToolPartWithState;
  return (
    typeof p === 'object' &&
    p !== null &&
    typeof p.type === 'string' &&
    p.type.startsWith('tool-') &&
    p.type !== 'tool-approval-request' &&
    p.type !== 'tool-approval-response'
  );
}

interface UseChatDerivedStateProps {
  messages: UIMessage[];
  isWaiting: boolean;
  showSuggestedMessages: boolean;
}

interface UseChatDerivedStateReturn {
  pendingToolCallConfirmation: boolean;
  toolProgress: { label: string } | null;
  showWorkingShimmer: boolean;
  suggestedMessages: string[];
}

export function useChatDerivedState({
  messages,
  isWaiting,
  showSuggestedMessages,
}: UseChatDerivedStateProps): UseChatDerivedStateReturn {
  // Check if there's a pending tool approval (tool-approval-request) — disable input until user Allow/Deny
  const pendingToolCallConfirmation = useMemo(() => {
    return messages.some((m) => {
      if (!m.parts) return false;
      return m.parts.some(
        (part: unknown) =>
          typeof part === 'object' &&
          part !== null &&
          'type' in part &&
          (part as ToolPartWithState).type === 'tool-approval-request' &&
          'toolCallId' in part &&
          (part as ToolPartWithState).toolCallId
      );
    });
  }, [messages]);

  // Derive "tool work in progress": last assistant message has a v6 typed
  // tool part (`tool-<name>`) whose state is still in flight. Narrate the
  // LAST unfinished one — that's the step actually running.
  const toolProgress = useMemo(() => {
    if (!isWaiting || messages.length === 0) return null;
    const last = messages[messages.length - 1];
    if (last.role !== 'assistant' || !last.parts?.length) return null;
    // UIMessagePart's literal-typed union doesn't structurally admit the loose
    // ToolPartWithState shape, so the predicate can't narrow via filter —
    // reinterpret the array once at this boundary instead.
    const inFlight = (last.parts as unknown as ToolPartWithState[]).filter(
      (p) => isToolPart(p) && IN_FLIGHT_TOOL_STATES.has(p.state ?? '')
    );
    const current = inFlight[inFlight.length - 1];
    if (!current) return null;
    const toolName = current.type?.replace('tool-', '') || current.toolName;
    if (!toolName) return { label: 'Running tools…' };
    return { label: getToolRunningLine(toolName, current.input ?? current.args) };
  }, [isWaiting, messages]);

  // Generic "working" when submitted but no tool info yet
  const showWorkingShimmer = useMemo(() => {
    return isWaiting && messages.length > 0;
  }, [isWaiting, messages.length]);

  // Memoize suggested messages
  const suggestedMessages = useMemo(() => {
    if (!showSuggestedMessages || messages.length > 0) return [];
    return [
      'Who should I follow up with this week?',
      'Find a contact',
      'List my open tickets',
    ];
  }, [showSuggestedMessages, messages.length]);

  return {
    pendingToolCallConfirmation,
    toolProgress,
    showWorkingShimmer,
    suggestedMessages,
  };
}
